import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "@/lib/useLang";
import { AGE_GROUPS, DISTRICTS, OCCUPATIONS, GENDERS } from "@/lib/profileOptions";
import Analytics from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROFILE_KEY = "kerala_speaks_profile_v1";

function loadProfile() {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveProfile(p) {
  try { localStorage.setItem(PROFILE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

function isValidEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());
}

/**
 * Step 2 of the submission flow.
 * Required: age_group, district, occupation.
 * Optional: gender, email.
 *
 * Two terminal actions:
 *   - SAVE INSIGHTS  → POST /api/profile (with validation), then onDone()
 *   - SKIP           → onDone() without posting (no record created)
 */
export const InsightsStep = ({ voterId, onDone, onPostAnother }) => {
  const { t } = useLang();
  const cached = loadProfile() || {};
  const [ageGroup, setAgeGroup] = useState(cached.age_group || "");
  const [district, setDistrict] = useState(cached.district || "");
  const [occupation, setOccupation] = useState(cached.occupation || "");
  const [gender, setGender] = useState(cached.gender || "");
  const [email, setEmail] = useState(cached.email || "");
  const [notifyOpt, setNotifyOpt] = useState(!!cached.notify_opt_in);
  const [status, setStatus] = useState("idle"); // idle | saving
  const [emailErr, setEmailErr] = useState("");
  const [requiredErr, setRequiredErr] = useState(false);

  useEffect(() => {
    if (!cached.age_group && voterId) {
      axios.get(`${API}/profile/${voterId}`).then((r) => {
        const p = r.data?.profile;
        if (!p) return;
        if (p.age_group) setAgeGroup(p.age_group);
        if (p.district) setDistrict(p.district);
        if (p.occupation) setOccupation(p.occupation);
        if (p.gender) setGender(p.gender);
        if (p.email) setEmail(p.email);
        if (typeof p.notify_opt_in === "boolean") setNotifyOpt(p.notify_opt_in);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voterId]);

  const missingRequired = !ageGroup || !district || !occupation;

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setEmailErr("");
    if (missingRequired) {
      setRequiredErr(true);
      return;
    }
    if (notifyOpt && !email.trim()) {
      setEmailErr("Please add your email to get notified about future civic questions.");
      return;
    }
    if (email && !isValidEmail(email)) {
      setEmailErr("Please enter a valid email or leave it blank.");
      return;
    }
    setStatus("saving");
    const body = {
      voter_id: voterId,
      age_group: ageGroup,
      district,
      occupation,
      gender: gender || undefined,
      email: email ? email.trim() : undefined,
      notify_opt_in: !!notifyOpt,
    };
    try { await axios.post(`${API}/profile`, body); } catch { /* best-effort */ }
    saveProfile({
      age_group: ageGroup, district, occupation, gender, email, notify_opt_in: notifyOpt,
    });
    Analytics.profileComplete(district, ageGroup, occupation, notifyOpt);
    setStatus("idle");
    onDone?.();
  };

  return (
    <section className="brutal-card p-5 md:p-7 fade-up" data-testid="insights-step">
      <p
        className="text-xs uppercase font-bold tracking-[0.3em]"
        style={{ color: "#E3000F" }}
        data-testid="step2-eyebrow"
      >
        {t("step2_eyebrow")}
      </p>
      <h2
        className="font-display uppercase leading-none mt-2 mb-2"
        style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.25rem)" }}
        data-testid="step2-heading"
      >
        {t("step2_heading")}
      </h2>
      <p className="text-sm md:text-base font-medium text-neutral-700 mb-1 max-w-2xl" data-testid="step2-sub">
        {t("step2_sub")}
      </p>
      <p className="text-xs font-bold text-neutral-600 mb-5 max-w-2xl" data-testid="step2-required-note">
        {t("step2_required_note")}
      </p>

      <form onSubmit={handleSave} noValidate data-testid="insights-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("step2_age_label")} required testId="field-age" t={t}>
            <Select
              value={ageGroup}
              onChange={(v) => { setAgeGroup(v); if (requiredErr) setRequiredErr(false); }}
              options={AGE_GROUPS}
              t={t}
              testId="age-select"
              invalid={requiredErr && !ageGroup}
            />
          </Field>
          <Field label={t("step2_district_label")} required testId="field-district" t={t}>
            <Select
              value={district}
              onChange={(v) => { setDistrict(v); if (requiredErr) setRequiredErr(false); }}
              options={DISTRICTS}
              t={t}
              testId="district-select"
              invalid={requiredErr && !district}
            />
          </Field>
          <Field label={t("step2_occupation_label")} required testId="field-occupation" t={t}>
            <Select
              value={occupation}
              onChange={(v) => { setOccupation(v); if (requiredErr) setRequiredErr(false); }}
              options={OCCUPATIONS}
              t={t}
              testId="occupation-select"
              invalid={requiredErr && !occupation}
            />
          </Field>
          <Field label={t("step2_gender_label")} testId="field-gender" t={t}>
            <Select value={gender} onChange={setGender} options={GENDERS} t={t} testId="gender-select" />
          </Field>
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase font-bold tracking-[0.25em] mb-1.5">
              {t("step2_email_label")}
            </label>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (emailErr) setEmailErr(""); }}
              placeholder={t("step2_email_placeholder")}
              disabled={status === "saving"}
              className="w-full border-4 border-black px-3 py-3 text-base font-medium outline-none"
              style={{ background: "#FDFBF7", color: "#0A0A0A", minHeight: 56 }}
              data-testid="email-input"
              aria-invalid={!!emailErr}
            />
            {emailErr && (
              <p className="text-xs font-bold mt-1" style={{ color: "#E3000F" }} data-testid="email-error">
                {emailErr}
              </p>
            )}
          </div>
        </div>

        {requiredErr && (
          <p
            className="text-xs font-bold mt-3"
            style={{ color: "#E3000F" }}
            role="alert"
            data-testid="required-error"
          >
            {t("step2_required_error")}
          </p>
        )}

        <div className="border-t-2 border-dashed border-neutral-300 mt-6 pt-5">
          <p className="text-xs uppercase font-extrabold tracking-[0.3em] text-neutral-600 mb-3" data-testid="notify-heading">
            {t("notify_heading")}
          </p>
          <label
            className="flex items-start gap-3 py-1.5 cursor-pointer select-none"
            data-testid="notify-opt-row"
          >
            <input
              type="checkbox"
              checked={notifyOpt}
              onChange={(e) => setNotifyOpt(e.target.checked)}
              className="mt-1 w-5 h-5 accent-black border-2 border-black shrink-0"
              data-testid="notify-opt"
            />
            <span className="text-sm md:text-base font-bold leading-snug">{t("notify_subscribe_label")}</span>
          </label>
          <p className="text-xs font-medium text-neutral-600 mt-1 ml-9 leading-snug">
            {t("notify_promise")}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button
            type="submit"
            disabled={status === "saving"}
            className="brutal-btn"
            data-testid="save-insights-btn"
          >
            {status === "saving" ? t("step2_saving_btn") : t("step2_save_btn")}
          </button>
          <button
            type="button"
            onClick={() => onDone?.()}
            disabled={status === "saving"}
            className="brutal-btn"
            style={{ background: "#FDFBF7", color: "#0A0A0A" }}
            data-testid="skip-insights-btn"
          >
            {t("step2_skip_btn")}
          </button>
          <button
            type="button"
            onClick={() => onPostAnother?.()}
            disabled={status === "saving"}
            className="text-xs uppercase font-extrabold tracking-[0.25em] underline-offset-2 hover:underline ml-auto self-center"
            data-testid="post-another-btn"
          >
            {t("step2_post_another_btn")} →
          </button>
        </div>
      </form>
    </section>
  );
};

const Field = ({ label, required, children, testId, t }) => (
  <div data-testid={testId}>
    <label className="block text-xs uppercase font-bold tracking-[0.25em] mb-1.5">
      {label}
      {required && (
        <span style={{ color: "#E3000F" }} className="ml-1" aria-hidden="true">
          {t ? t("required_mark") : "*"}
        </span>
      )}
    </label>
    {children}
  </div>
);

const Select = ({ value, onChange, options, t, testId, invalid }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full border-4 px-3 py-3 text-base font-bold outline-none"
    style={{
      background: "#FDFBF7",
      color: value ? "#0A0A0A" : "#6B6B6B",
      borderColor: invalid ? "#E3000F" : "#0A0A0A",
      minHeight: 56,
    }}
    data-testid={testId}
    aria-invalid={invalid}
  >
    <option value="">{t("placeholder_select")}</option>
    {options.map((o) => (
      <option key={o.id} value={o.id}>
        {t(o.key)}
      </option>
    ))}
  </select>
);

export default InsightsStep;
